import React from 'react';
import Image from 'next/image';
import { css } from 'styled-system/css';

import type { SiteCopy } from '@shaojiang/content';

/** Shared by the route loading boundary and the layout's Suspense fallback. */
export function PageLoader({ copy }: { copy: SiteCopy['loading'] }) {
  return (
    <div
      role='status'
      aria-live='polite'
      aria-atomic='true'
      className={css({
        position: 'fixed',
        inset: '0',
        zIndex: 'modal',
        display: 'grid',
        placeItems: 'center',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        bg: 'bg.canvas',
        bgGradient: 'canvas',
        color: 'fg.default',
        p: '6',
      })}
    >
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: '3',
          maxW: 'full',
          py: '6',
        })}
      >
        <p aria-hidden='true' className={css({ textStyle: 'kicker', color: 'fg.hot' })}>
          {copy.kicker}
        </p>
        <div
          aria-hidden='true'
          className={css({
            position: 'relative',
            isolation: 'isolate',
            w: 'min(18rem, 72vw)',
            aspectRatio: '1',
            my: '2',
            _before: {
              content: '""',
              position: 'absolute',
              inset: '10% 4% 2%',
              zIndex: '-1',
              bg: 'tone.sky.bg',
              borderRadius: '46% 54% 51% 49% / 54% 46% 54% 46%',
              _nebula: {
                bg: 'brand.subtle',
                borderRadius: 'full',
                boxShadow: '0 0 80px 12px {colors.glow}',
              },
            },
          })}
        >
          {/* A picture source stops the animation without waiting for client JavaScript. */}
          <picture>
            <source
              media='(prefers-reduced-motion: reduce)'
              srcSet='/images/avatar-typing-still.webp'
            />
            <Image
              src='/images/avatar-typing.webp'
              alt=''
              width={384}
              height={384}
              unoptimized
              loading='eager'
              fetchPriority='high'
              className={css({ display: 'block', w: 'full', h: 'auto' })}
            />
          </picture>
        </div>
        <p className={css({ textStyle: 'cardTitle' })}>{copy.title}</p>
        <p className={css({ textStyle: 'sm', color: 'fg.muted' })}>{copy.description}</p>
      </div>
    </div>
  );
}
